/* global RequestInit */
/* global RequestInfo */

import { v4 as uuidv4 } from "uuid"
import {
  extractErrorMessageErrorCodeAndTranslationKey,
  fallbackError,
} from "../util/error-handler"
import HeapsError from "../util/heaps-error"
import {
  getCachedAccountType,
  getCachedUser,
  notifySessionRefused,
  removeCachedUser,
  setCachedUser,
} from "./cached-user"
import { translationKeys } from "../util/translationKeys"
import { errorCodes } from "./error-codes"
import { GUEST_SIGNIN_PATH } from "./constants"
import { captureException } from "./exception-logger"
import { classifyApiUrl, toEndpoint } from "./api-error-classification"
import { adoptHandoverSession } from "./access-token-handover"

const HEAPS_BACKEND_URL =
  process.env.GATSBY_HEAPS_BACKEND_URL || "https://drinks-staging.heapsapp.com"
const API_KEY =
  process.env.GATSBY_HEAPS_API_KEY || "f02bed37-f90f-4b60-87d9-b0d93a248f46"
const API_VERSION = process.env.GATSBY_HEAPS_API_VERSION || "20241028"
const APP_ID = process.env.GATSBY_HEAPS_APP_ID || "unknown_default"

// One re-auth per request: if a fresh guest session is still refused, the
// problem is not the session
const MAX_AUTH_RETRIES = 1

const defaultHeaders = {
  "X-Drinks-Api-Key": API_KEY,
  "X-Drinks-Version": API_VERSION,
  "X-Drinks-App-Id": APP_ID,
  "Content-Type": "application/json",
}

const getHeaders = () => {
  if (
    typeof window === "undefined" ||
    !localStorage ||
    typeof localStorage === "undefined"
  ) {
    return defaultHeaders
  }

  const headers: any = { ...defaultHeaders }
  const user = getCachedUser()
  if (user?.access_token) {
    headers["Authorization"] = `Bearer ${user?.access_token}`
  }
  headers["X-Drinks-Idempotency-Key"] = uuidv4()

  return headers
}

const fetchWithBase = (
  url: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> => {
  return fetch(HEAPS_BACKEND_URL + url.toString(), init)
}

let guestSessionPromise: Promise<void> | null = null

const signinGuest = async (): Promise<void> => {
  const request = { method: "POST", endpoint: GUEST_SIGNIN_PATH }
  try {
    const response = await fetchWithBase(GUEST_SIGNIN_PATH, {
      method: "POST",
      headers: defaultHeaders,
    })
    if (!response.ok) {
      throw new HeapsError({
        ...fallbackError,
        errorCode: "guest_signin_failed",
        statusCode: response.status,
        request,
      })
    }
    const { data } = await response.json()
    // A sign-in that landed while this was in flight wins over the guest
    if (!getCachedUser()?.access_token) {
      setCachedUser(data)
    }
  } catch (error: any) {
    // Same handling as any other failed request. Logged here, once - every
    // request waiting on this signin gets the same error
    const heapsError =
      error instanceof HeapsError
        ? error
        : new HeapsError({
            ...fallbackError,
            errorMessage: error?.message || fallbackError.errorMessage,
            request,
          })
    captureException(heapsError, classifyApiUrl(GUEST_SIGNIN_PATH))
    throw heapsError
  } finally {
    guestSessionPromise = null
  }
}

// Guest signin shared by every request that finds no token at the same time.
// An async function runs synchronously up to its first await, so the first
// caller assigns the promise before anything can interleave - a page firing
// several queries at once mints one session and writes it once.
export const ensureGuestSession = async (): Promise<void> => {
  if (getCachedUser()?.access_token) {
    return
  }
  guestSessionPromise ??= signinGuest()
  await guestSessionPromise
}

type FetchHookOptions = {
  anonymous: boolean
  /** Default for every request made through this client; a call can override */
  on401?: On401
}

// What a session-backed request does when the API refuses its token.
// "reauth-guest": start a fresh guest session and retry - right while browsing,
// where nothing is tied to the session. "throw": surface it to the caller as a
// SESSION_EXPIRED_* HeapsError and leave the dead token in place - right on
// checkout and order status, where a guest's order died with the session and a
// user can sign in again and keep theirs. Minting a guest there would only
// turn the 401 into a 404 on someone else's order.
export type On401 = "reauth-guest" | "throw"

type FetchRequestOptions = {
  data?: object
  retries?: number
  headers?: any
  followRedirect?: boolean
  on401?: On401
  /** Internal: how many times this request has already re-authenticated */
  authRetries?: number
}

export const useFetchRequest = ({
  anonymous,
  on401: defaultOn401 = "reauth-guest",
}: FetchHookOptions) => {
  const fetchRequest = async <T = any>(
    method: string,
    url: string,
    options?: FetchRequestOptions
  ): Promise<T> => {
    const data = options?.data
    const retries = options?.retries || 0
    const headers = options?.headers
    const followRedirect = options?.followRedirect || false

    // A session-backed request never leaves without a token. Without this,
    // queries that fire on page load race the guest signin, go out bare and
    // come back 401.
    if (!anonymous) {
      // The post-payment redirect is handed the session that owns the order;
      // adopting it here keeps the guest signin below from replacing it
      adoptHandoverSession()
      if (!getCachedUser()?.access_token) {
        await ensureGuestSession()
      }
    }
    // The token getHeaders() puts on this request, to tell on a 401 whether
    // another request has already replaced the session, and whose it was
    const sentToken = getCachedUser()?.access_token
    const sentAccountType = getCachedAccountType()

    const config: RequestInit = {
      method,
      headers: {
        ...(anonymous ? defaultHeaders : getHeaders()),
        ...headers,
      },
      redirect: followRedirect ? "follow" : "manual",
    }

    if (data) {
      config.body = JSON.stringify(data)
    }

    try {
      const fetchResponse = await fetchWithBase(url, config)

      if (!fetchResponse.ok) {
        // This block of code catches the requests that are cancelled due to followRedirect === false
        if (fetchResponse.type === "opaqueredirect") {
          // There was a request that was redirected. Instead of following redirect, we do the refetch ourselves. Returning data will trigger react-query onSuccss
          return new Promise((resolve) => {
            resolve({ data: {} } as any)
          })
        }

        // Retry if status code is 409 or 423. Max 2 retries.
        // Send the same request again: keep the caller's options and the
        // idempotency key already sent. Only the key - pinning the
        // Authorization header would make a later 401 retry send the dead
        // token instead of the new session's.
        else if (
          (fetchResponse.status === 409 || fetchResponse.status === 423) &&
          retries < 2
        ) {
          const idempotencyKey = (config.headers as Record<string, string>)[
            "X-Drinks-Idempotency-Key"
          ]
          return await fetchRequest(method, url, {
            ...options,
            headers: idempotencyKey
              ? { ...headers, "X-Drinks-Idempotency-Key": idempotencyKey }
              : headers,
            retries: retries + 1,
          })
        }

        // Unauthorized: the token was refused. Anonymous endpoints (signin,
        // signup, ...) answer 401 as a business outcome, so only
        // session-backed requests recover here. There are no refresh tokens.
        else if (fetchResponse.status === 401 && !anonymous) {
          const on401 = options?.on401 ?? defaultOn401
          const authRetries = options?.authRetries ?? 0
          const request = { method, endpoint: toEndpoint(url) }

          if (on401 === "throw") {
            // The session was replaced while this was in flight (e.g. the user
            // signed in again) - the refusal was for the old one, so retry
            const currentToken = getCachedUser()?.access_token
            if (
              currentToken &&
              currentToken !== sentToken &&
              authRetries < MAX_AUTH_RETRIES
            ) {
              return await fetchRequest(method, url, {
                ...options,
                authRetries: authRetries + 1,
              })
            }

            const kind = sentAccountType
            notifySessionRefused(kind, sentToken ?? null)
            // No errorMessage so getErrorMessage falls through to the
            // translated copy wherever this lands
            throw new HeapsError({
              errorMessage: "",
              translationKey: translationKeys.webSessionExpiredDescription,
              errorCode:
                kind === "user"
                  ? errorCodes.SESSION_EXPIRED_USER
                  : errorCodes.SESSION_EXPIRED_GUEST,
              statusCode: 401,
              request,
            })
          }

          if (authRetries >= MAX_AUTH_RETRIES) {
            const heapsError = new HeapsError({
              errorMessage: fallbackError.errorMessage,
              translationKey: fallbackError.translationKey,
              errorCode: "unauthorized",
              statusCode: 401,
              request,
            })
            // A fresh guest session is still refused - that is not a session
            // problem
            captureException(heapsError, classifyApiUrl(url))
            throw heapsError
          }

          // A request that went out with the same token may have been refused
          // first and already started a new session - clearing now would throw
          // that one away and mint another. Only the first refusal clears;
          // the rest retry on whatever session is current
          if (getCachedUser()?.access_token === sentToken) {
            // The order belonged to the dead session. The cart is kept: it
            // lives only in the browser, and the menu keeps showing it
            removeCachedUser()
            localStorage?.removeItem("hg::cache::order")
          }
          await ensureGuestSession()
          return await fetchRequest(method, url, {
            ...options,
            authRetries: authRetries + 1,
          })
        }

        // Regular API error
        else {
          const error = await fetchResponse.json()
          const { errorMessage, translationKey, errorCode } =
            extractErrorMessageErrorCodeAndTranslationKey(error)
          // propagate error so that it can be handled appropriately on different places in the app with errorMessage
          // Access error in functions with getErrorMessage util function in /error-handler.ts
          const heapsError = new HeapsError({
            errorMessage,
            translationKey,
            errorCode,
            statusCode: fetchResponse.status,
            request: {
              method,
              endpoint: toEndpoint(url),
              responseBody: error,
            },
          })
          captureException(heapsError, classifyApiUrl(url))
          throw heapsError
        }
      }

      const responseContentType = fetchResponse.headers.get("content-type")
      const responseHasBody =
        responseContentType?.startsWith("application/json")

      // API returns 200 on POST but no response body
      // When fetch is trying to deserialize empty response body, it results in error
      // We want to continue with success flow
      if (!responseHasBody) {
        return new Promise((resolve) => {
          resolve({ data: {} } as any)
        })
      }

      return fetchResponse.json()
    } catch (error: any) {
      // If it's the error we threw in try {} block, just propagate
      if (error instanceof HeapsError) {
        throw error
      }

      // Fetch API call results in error in two cases: network error or cors error
      // So this should be one of the two. Throw generic error
      const { errorMessage, translationKey, errorCode } = fallbackError

      // Propagate error so that it can be handled appropriately on different places in the app with errorMessage
      // Access error in functions with getErrorMessage util function in /error-handler.ts
      // Try sending error.message since it's probably more relevant and better hint
      const heapsError = new HeapsError({
        errorMessage: error?.message || errorMessage,
        translationKey,
        errorCode,
        request: {
          method,
          endpoint: toEndpoint(url),
        },
      })
      captureException(heapsError, classifyApiUrl(url))
      throw heapsError
    }
  }

  return fetchRequest
}
